import React, { useState, useEffect } from 'react';
import api from './api';

const EmployeeMaster = () => {
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        name: '', mobile: '', empCode: '', department: '', designation: '', 
        salaryType: 'F', basicSalary: '', defaultPieceRate: '', 
        paymentMode: 'Cash', // NEW: Defaults to Cash
        hasESIC: false, hasPF: false, hasPT: false
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const deptRes = await api.get('/hr-masters/departments');
            const desigRes = await api.get('/hr-masters/designations');
            setDepartments(deptRes.data.data || []);
            setDesignations(desigRes.data.data || []);

            const empRes = await api.get('/factory-workers'); 
            const empData = Array.isArray(empRes.data) ? empRes.data : (empRes.data.data || []);
            setEmployees(empData);
        } catch (err) { console.error("Error loading data:", err); }
        finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.post('/factory-workers', form);
            alert("✅ Employee Added!");
            setForm({
                name: '', mobile: '', empCode: '', department: '', designation: '', 
                salaryType: 'F', basicSalary: '', defaultPieceRate: '', 
                paymentMode: 'Cash',
                hasESIC: false, hasPF: false, hasPT: false
            });
            fetchData();
        } catch (err) { alert("Error adding employee. Check console for details."); }
    };

    return (
        <div className="container mt-4 mb-5">
            <h3 className="mb-4 fw-bold text-primary">👷 Factory Employee Profiles</h3>

            <div className="row">
                {/* --- LEFT: ADD EMPLOYEE FORM --- */}
                <div className="col-lg-4 mb-4">
                    <div className="card shadow-sm border-primary">
                        <div className="card-header bg-primary text-white fw-bold">➕ Add New Employee</div>
                        <div className="card-body bg-light">
                            <form onSubmit={handleSubmit}>
                                {/* Basic Info */}
                                <div className="row g-2 mb-3">
                                    <div className="col-8">
                                        <label className="small fw-bold">Full Name *</label>
                                        <input type="text" className="form-control" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                                    </div>
                                    <div className="col-4">
                                        <label className="small fw-bold">Emp Code</label>
                                        <input type="text" className="form-control" value={form.empCode} onChange={e => setForm({...form, empCode: e.target.value})} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="small fw-bold">Mobile</label>
                                    <input type="text" className="form-control" value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} />
                                </div>

                                {/* Masters Dropdowns */}
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <label className="small fw-bold">Department</label>
                                        <select className="form-select" value={form.department} onChange={e => setForm({...form, department: e.target.value})}>
                                            <option value="">-- Select --</option>
                                            {departments.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="small fw-bold">Designation</label>
                                        <select className="form-select" value={form.designation} onChange={e => setForm({...form, designation: e.target.value})}>
                                            <option value="">-- Select --</option>
                                            {designations.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* Salary Structure (SMART UI) */}
                                <hr />
                                <h6 className="fw-bold text-secondary">💰 Salary Structure</h6>
                                <div className="mb-3">
                                    <label className="small fw-bold">Pay Type</label>
                                    <select className="form-select border-warning" value={form.salaryType} onChange={e => setForm({...form, salaryType: e.target.value})}>
                                        <option value="F">Fixed Monthly (F)</option>
                                        <option value="V">Variable / Piece Rate (V)</option>
                                        <option value="MV">Multi-Variable (MV)</option>
                                    </select>
                                </div>

                                {/* Conditional Salary Inputs */}
                                {form.salaryType === 'F' && (
                                    <div className="mb-3">
                                        <label className="small fw-bold">Monthly Salary (₹)</label>
                                        <input type="number" className="form-control" value={form.basicSalary} onChange={e => setForm({...form, basicSalary: e.target.value})} placeholder="e.g. 30000" />
                                    </div>
                                )}
                                {form.salaryType === 'V' && (
                                    <div className="mb-3">
                                        <label className="small fw-bold">Default Rate per Piece (₹)</label>
                                        <input type="number" className="form-control" value={form.defaultPieceRate} onChange={e => setForm({...form, defaultPieceRate: e.target.value})} placeholder="e.g. 20" />
                                    </div>
                                )}
                                {form.salaryType === 'MV' && (
                                    <div className="alert alert-info py-2 small mb-3">
                                        ℹ️ Multi-Variable rates will be pulled from the Process Masters.
                                    </div>
                                )}

                                {/* NEW: Payment Mode */}
                                <div className="mb-3">
                                    <label className="small fw-bold">Payment Mode</label>
                                    <select className="form-select border-info" value={form.paymentMode} onChange={e => setForm({...form, paymentMode: e.target.value})}>
                                        <option value="Cash">💵 Cash (No Taxes Apply)</option>
                                        <option value="Bank">🏦 Bank / On Paper (Taxes Apply)</option>
                                    </select>
                                </div>

                                {/* Compliance */}
                                <hr />
                                <div className="d-flex justify-content-between mb-4">
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" checked={form.hasPF} onChange={e => setForm({...form, hasPF: e.target.checked})} />
                                        <label className="form-check-label small fw-bold">PF</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" checked={form.hasESIC} onChange={e => setForm({...form, hasESIC: e.target.checked})} />
                                        <label className="form-check-label small fw-bold">ESIC</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" checked={form.hasPT} onChange={e => setForm({...form, hasPT: e.target.checked})} />
                                        <label className="form-check-label small fw-bold">Prof. Tax</label>
                                    </div>
                                </div>

                                <button type="submit" className="btn btn-primary w-100 fw-bold">Save Employee</button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* --- RIGHT: EMPLOYEE TABLE --- */}
                <div className="col-lg-8">
                    <div className="card shadow-sm border-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-dark">
                                    <tr>
                                        <th>Code</th>
                                        <th>Name</th>
                                        <th>Dept</th>
                                        <th className="text-center">Type</th>
                                        <th className="text-end">Base Pay & Mode</th>
                                        <th className="text-center">Compliance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="6" className="text-center py-4">Loading...</td></tr>
                                    ) : employees.length === 0 ? (
                                        <tr><td colSpan="6" className="text-center py-4 text-muted">No employees found.</td></tr>
                                    ) : (
                                        employees.map(emp => (
                                            <tr key={emp._id}>
                                                <td className="text-muted fw-bold">{emp.empCode || '-'}</td>
                                                <td className="fw-bold text-primary">{emp.name}</td>
                                                <td>{emp.department ? emp.department.name : '-'}</td>
                                                <td className="text-center">
                                                    <span className={`badge ${emp.salaryType === 'F' ? 'bg-primary' : emp.salaryType === 'V' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                        {emp.salaryType || 'F'}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-bold">
                                                    {emp.salaryType === 'F' ? `₹${emp.basicSalary}` : emp.salaryType === 'V' ? `₹${emp.defaultPieceRate}/pc` : 'MV'}
                                                    <br/>
                                                    <span className={`badge ${emp.paymentMode === 'Bank' ? 'bg-info text-dark' : 'bg-secondary'} mt-1`}>
                                                        {emp.paymentMode || 'Cash'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    {emp.hasPF && <span className="badge bg-secondary me-1">PF</span>}
                                                    {emp.hasESIC && <span className="badge bg-secondary me-1">ESI</span>}
                                                    {emp.hasPT && <span className="badge bg-secondary">PT</span>}
                                                    {(!emp.hasPF && !emp.hasESIC && !emp.hasPT) && <span className="text-muted small">-</span>}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeMaster;